import {
  Box,
  Spinner,
  Heading,
  Text,
  Tabs,
  VStack,
  Flex,
  IconButton,
} from '@chakra-ui/react';
import { useQueryClient } from '@tanstack/react-query';
import {
  Ellipsis, Trash2, SquarePen,
} from 'lucide-react';
import React, { useState, useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useNavigate } from 'react-router';

import { updateItem } from '@app/actions/common';
import SimpleMenu from '@app/components/chakra/menus/simple-menu';
import { toaster } from '@app/components/chakra/snippets/toaster';
import { useGroupsQuery } from '@app/services/group';
import { type Group } from '@app/types/api/group';

import DeleteGroupDialog from './delete-group-dialog';
import MemberTab from './member-tab';
import SubgroupTab from './subgroup-tab';
import GroupDialog from '../dialog/group-dialog';

const GroupDetailView = () => {
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const dispatch = useDispatch();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { id } = useParams();
  const { data: groups, isLoading } = useGroupsQuery(id ? { ids: [id] } : {});
  const [activeTab, setActiveTab] = useState('members');

  const group = groups?.pages[0].groups[0];

  useEffect(() => {
    setActiveTab('members');
  }, [id]);

  const handleMenuSelect = useCallback((details: { value: string }) => {
    if (details.value === 'edit') {
      setIsDialogOpen(true);
    }
    if (details.value === 'delete') {
      setIsDeleting(true);
    }
  }, [setIsDialogOpen]);

  const menuOptions = [
    {
      value: 'edit',
      label: 'Edit Group',
      leftIcon: <SquarePen />,
    },
    {
      value: 'delete',
      label: 'Delete Group',
      leftIcon: <Trash2 />,
      color: 'red.500',
    },
  ];

  const handleDelete = async () => {
    try {
      await dispatch(updateItem('GROUP', { archived: true }, group?.id, 'group'));
      setIsDeleting(false);
      navigate('/usermgmt/groups');
      toaster.create({
        title: 'Group deleted successfully',
        type: 'success',
      });
      queryClient.invalidateQueries({ queryKey: ['groups'] });
    } catch (error) {
      toaster.create({
        title: 'Failed to delete group',
        type: 'error',
      });
    }
  };

  const tabProps = {
    h: '60px',
    _selected: {
      '--indicator-color': 'colors.marleyRed.500',
      '--indicator-thickness': '4px',
    },
    whiteSpace: 'nowrap',
  };
  if (isLoading) {
    return <Spinner data-testid="loading-spinner" />;
  }

  return (
    <VStack
      w="100%"
      alignItems="flex-start"
      gap={4}
      flex="1"
      display="flex"
      flexDirection="column"
      overflow="hidden"
    >
      <GroupDialog
        isOpen={isDialogOpen}
        onOpenChange={(value) => setIsDialogOpen(value.open)}
        editGroup={group}
      />
      <DeleteGroupDialog
        isOpen={isDeleting}
        onOpenChange={(value) => setIsDeleting(value.open)}
        onDelete={handleDelete}
      />
      <Flex justifyContent="space-between" w="100%">
        <Heading size="2xl">{group?.name}</Heading>
        <SimpleMenu
          onSelect={handleMenuSelect}
          options={menuOptions}
          menuTrigger={(
            <IconButton variant="ghost">
              <Ellipsis />
            </IconButton>
                )}
        />
      </Flex>
      <Text>{group?.description}</Text>
      <Tabs.Root
        value={activeTab}
        onValueChange={(details: { value: string }) => setActiveTab(details.value)}
        variant="line"
        display="flex"
        flexDirection="column"
        w="100%"
        height="calc(100vh - 300px)"
      >
        <Box bg="white" flexShrink={0}>
          <Tabs.List>
            <Tabs.Trigger {...tabProps} value="members">
              {group?.count}
              {' '}
              Members
            </Tabs.Trigger>
            <Tabs.Trigger {...tabProps} value="subgroups">
              {group?.subgroups?.length}
              {' '}
              Sub-Group
              {(group?.subgroups?.length ?? 0) === 1 ? '' : 's'}
            </Tabs.Trigger>
          </Tabs.List>
        </Box>
        <Box flex="1" overflow="hidden">
          <Tabs.Content value="members" h="100%">
            <MemberTab
              members={group?.members}
              leads={group?.leads}
              subgroups={group?.subgroups}
              count={group?.count}
            />
          </Tabs.Content>
          <Tabs.Content value="subgroups">
            <SubgroupTab ids={group?.subgroups?.map((subgroup: Group) => subgroup.id) ?? []} />
          </Tabs.Content>
        </Box>
      </Tabs.Root>
    </VStack>
  );
};

export default GroupDetailView;
